import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMarketingCampaigns } from '@/hooks/use-marketing';
import {
  campaignStatusLabel, campaignStatusVariant, formatPeriod, promotionLabel,
  type CampaignStatus, type PromotionType,
} from '@/lib/marketing';

const LOCALES: Record<string, string> = { uz: 'uz-UZ', ru: 'ru-RU', en: 'en-US' };

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

export default function AdminMarketingCalendar() {
  const { t, i18n } = useTranslation();
  const locale = LOCALES[(i18n.language || 'uz').slice(0, 2)] ?? 'uz-UZ';
  const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) => t(`mkt_calendar.wd_${d}`));
  const [view, setView] = useState<'month' | 'week' | 'list'>('month');
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const { data: campaigns = [] } = useMarketingCampaigns();

  const covers = (c: any, day: Date) => {
    const s = startOfDay(new Date(c.start_at)).getTime();
    const e = startOfDay(new Date(c.end_at)).getTime();
    const t = day.getTime();
    return t >= s && t <= e;
  };

  const days = useMemo(() => {
    if (view === 'week') {
      const start = addDays(anchor, -mondayIndex(anchor));
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = addDays(first, -mondayIndex(first));
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [anchor, view]);

  const shift = (dir: -1 | 1) => {
    if (view === 'week') return setAnchor(addDays(anchor, dir * 7));
    setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };

  const title = view === 'week'
    ? `${days[0]?.toLocaleDateString(locale)} — ${days[6]?.toLocaleDateString(locale)}`
    : anchor.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  return (
    <div className="mx-auto max-w-7xl animate-fade-in p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('mkt_calendar.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('mkt_calendar.subtitle')}</p>
        </div>
        <Tabs value={view} onValueChange={(v) => setView(v as any)}>
          <TabsList>
            <TabsTrigger value="month">{t('mkt_calendar.month')}</TabsTrigger>
            <TabsTrigger value="week">{t('mkt_calendar.week')}</TabsTrigger>
            <TabsTrigger value="list">{t('mkt_calendar.list')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view !== 'list' && (
        <div className="mb-3 flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => shift(-1)}><ChevronLeft size={16} /></Button>
          <Button size="icon" variant="outline" onClick={() => shift(1)}><ChevronRight size={16} /></Button>
          <span className="text-sm font-medium capitalize text-foreground">{title}</span>
          <Button size="sm" variant="ghost" onClick={() => setAnchor(startOfDay(new Date()))}>{t('mkt_calendar.today')}</Button>
        </div>
      )}

      {view === 'list' ? (
        <div className="rounded-xl border border-border bg-card">
          {campaigns.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('mkt_calendar.empty')}</p>
          ) : (
            <div className="divide-y divide-border">
              {campaigns.map((c: any) => (
                <Link key={c.id} to={`/admin/marketing/campaigns/${c.id}`} className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/30">
                  <div>
                    <p className="text-sm font-medium text-foreground">{c.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {promotionLabel[c.promotion_type as PromotionType]} · {formatPeriod(c.start_at, c.end_at)}
                    </p>
                  </div>
                  <Badge variant={campaignStatusVariant[c.status as CampaignStatus]}>
                    {campaignStatusLabel[c.status as CampaignStatus]}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="grid grid-cols-7 border-b border-border bg-muted/50">
            {WEEKDAYS.map((d) => (
              <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const dayCampaigns = campaigns.filter((c: any) => covers(c, day));
              const outside = view === 'month' && day.getMonth() !== anchor.getMonth();
              const today = day.getTime() === startOfDay(new Date()).getTime();
              return (
                <div key={day.toISOString()} className={`min-h-[96px] border-b border-r border-border p-2 ${outside ? 'bg-muted/30' : ''}`}>
                  <p className={`mb-1 text-xs ${today ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>{day.getDate()}</p>
                  <div className="space-y-1">
                    {dayCampaigns.slice(0, 3).map((c: any) => (
                      <Link key={c.id} to={`/admin/marketing/campaigns/${c.id}`}
                        className="block truncate rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-foreground hover:bg-primary/20">
                        {c.name}
                      </Link>
                    ))}
                    {dayCampaigns.length > 3 && (
                      <p className="text-[10px] text-muted-foreground">+{dayCampaigns.length - 3}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
